import VikenLogo from '@assets/sidebar-menu-button.svg?react'
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@components/ui/sidebar.tsx'
import { Link } from '@tanstack/react-router'
import { useTranslations } from '@utils/i18n-to-migrate/useTranslations'
import {
  ExternalLink,
  FlaskConical,
  Search,
  Shapes,
  Smartphone,
  Tractor,
  TreePine,
  UserRound,
} from 'lucide-react'
import type * as React from 'react'
import { environment } from '@/config/environment'

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslations()
  const { isMobile, isOverlay, isXl, setOpen, setOpenMobile } = useSidebar()

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false)
    } else if (isOverlay || isXl) {
      setOpen(false)
    }
  }

  const navMain = [
    {
      title: 'SAMBA',
      hide: true,
      items: [
        {
          title: t('app-sidebar.navigation.search'),
          url: '/search',
          icon: <Search size={16} />,
        },
      ],
    },
    {
      title: t('app-sidebar.navigation.aktorer'),
      hide: false,
      items: [
        {
          title: t('app-sidebar.navigation.skogeier'),
          url: '/forestowner/search',
          activeOnPaths: ['/forestowner'],
          icon: <UserRound size={16} />,
        },
        {
          title: t('app-sidebar.navigation.contractor'),
          url: '/contractor/search',
          activeOnPaths: ['/contractor'],
          icon: <Tractor size={16} />,
        },
        {
          title: t('app-sidebar.navigation.timberbuyer'),
          url: '/timberbuyer/search',
          activeOnPaths: ['/timberbuyer'],
          icon: <TreePine size={16} />,
        },
        ...(environment.showFramework
          ? [
              {
                title: t('app-sidebar.navigation.framework'),
                url: '/framework/search',
                activeOnPaths: ['/framework'],
                icon: <FlaskConical size={16} />,
              },
            ]
          : []),
      ],
    },
    {
      title: t('app-sidebar.navigation.shortcuts'),
      items: [
        {
          title: t('app-sidebar.navigation.skogmodul'),
          url: 'https://intranettest-vikenskog.msappproxy.net/',
          target: '_blank',
          icon: <ExternalLink size={16} />,
        },
      ],
    },
    {
      title: 'BULK_SMS',
      hide: true,
      className: 'mt-auto',
      items: [
        {
          title: t('app-sidebar.assortment'),
          url: '/assortment/search',
          activeOnPaths: ['/assortment'],
          icon: <Shapes size={16} />,
        },
        {
          title: t('app-sidebar.bulkSms'),
          url: '/bulk-sms',
          icon: <Smartphone size={16} />,
        },
      ],
    },
  ]

  const itemIsActive = (i: {
    url: string
    activeOnPaths?: string[]
    searchParams?: Record<string, string>
  }) => {
    if (i.url.startsWith('http')) return false
    const pathMatches = location.pathname === i.url || location.pathname.startsWith(`${i.url}/`)
    if (pathMatches) {
      if (i.searchParams) {
        const current = new URLSearchParams(window.location.search)
        return Object.entries(i.searchParams).every(([k, v]) => current.get(k) === v)
      }
      return true
    }
    return (
      i.activeOnPaths?.some(
        (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
      ) ?? false
    )
  }

  const labeledGroupActive = navMain
    .filter((g) => !g.hide)
    .flatMap((g) => g.items)
    .some(itemIsActive)

  return (
    <Sidebar {...props} collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <a href="/#">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg text-sidebar-primary-foreground">
                  <VikenLogo title="Viken Logo" />
                </div>
                <div className="flex flex-col gap-0.5 leading-none group-data-[collapsible=icon]:hidden">
                  <span className="font-semibold text-lg font-family-font-headings">
                    {t('brand.productName')}
                  </span>
                </div>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {navMain.map((navGroup) => (
          <SidebarGroup key={navGroup.title} className={navGroup.className}>
            <SidebarGroupLabel className={navGroup.hide ? 'hidden' : ''}>
              {navGroup.title}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navGroup.items.map((item) => {
                  const isActive = itemIsActive(item) && (!navGroup.hide || !labeledGroupActive)
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton isActive={isActive} asChild>
                        <Link
                          to={item.url}
                          {...('searchParams' in item && {
                            search: item.searchParams as never,
                          })}
                          {...('target' in item && { target: item.target })}
                          onClick={handleNavClick}
                        >
                          {item.icon}
                          {item.title}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarRail />
      <Card className="mx-2 mb-2 gap-1.5 group-data-[collapsible=icon]:hidden short:hidden">
        <CardHeader className="pb-0">
          <CardTitle className="font-family-font-headings text-black text-sm font-semibold">
            Gi en tilbakemelding
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <span className="text-sm text-muted-foreground leading-tight">
            Vi ønsker å høre fra dere. Skriv gjerne til oss hvis du finner en feil eller har forslag
            til forbedringer.
          </span>
          <div className="mt-4">
            <a
              href="https://forms.office.com/e/8pmy87qxu4"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 w-full"
            >
              <div className="flex gap-2 items-center">
                <ExternalLink size={16} />
                Skriv tilbakemelding
              </div>
            </a>
          </div>
        </CardContent>
      </Card>
    </Sidebar>
  )
}
